import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { listClients } from '../api/clients'
import { listProspects } from '../api/prospects'
import { listDeals } from '../api/deals'
import { getRecentItems, getRecentItemRoute, type RecentItem } from '../utils/recentItems'
import type { Client, Prospect, Deal } from '../types'
import { processLoadResults } from '../utils/apiLoadTracker'
import { capitalize } from '../utils/formatters'
import styles from './CommandPalette.module.css'

type SearchResult = {
  type: 'client' | 'prospect' | 'deal'
  id: string
  title: string
  subtitle: string
  url: string
}

type QuickAction = {
  id: string
  label: string
  icon: React.ReactNode
  shortcut: string
  action: () => void
}

type PaletteItem = {
  id: string
  type: 'action' | 'recent' | 'result'
  label: string
  sublabel?: string
  icon: React.ReactNode
  shortcut?: string
  onSelect: () => void
}

export function CommandPalette() {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [recentItems, setRecentItems] = useState<RecentItem[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [chordKey, setChordKey] = useState<string | null>(null)
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)

  // Quick actions
  const quickActions: QuickAction[] = useMemo(() => [
    {
      id: 'new-deal',
      label: 'New Deal',
      icon: <FileIcon />,
      shortcut: 'N → D',
      action: () => {
        navigate('/deals/new')
        setIsOpen(false)
      }
    },
    {
      id: 'new-athlete',
      label: 'New Athlete',
      icon: <UserIcon />,
      shortcut: 'N → A',
      action: () => {
        navigate('/clients/new')
        setIsOpen(false)
      }
    },
    {
      id: 'new-task',
      label: 'New Task',
      icon: <CheckIcon />,
      shortcut: 'N → T',
      action: () => {
        navigate('/tasks/new')
        setIsOpen(false)
      }
    },
    {
      id: 'new-prospect',
      label: 'New Prospect',
      icon: <UserPlusIcon />,
      shortcut: 'N → P',
      action: () => {
        navigate('/prospects/new')
        setIsOpen(false)
      }
    },
    {
      id: 'new-contract',
      label: 'New Contract',
      icon: <FileIcon />,
      shortcut: 'N → C',
      action: () => {
        navigate('/contracts/new')
        setIsOpen(false)
      }
    },
    {
      id: 'go-calendar',
      label: 'Calendar',
      icon: <CalendarIcon />,
      shortcut: 'G → C',
      action: () => {
        navigate('/calendar')
        setIsOpen(false)
      }
    },
    {
      id: 'go-compliance',
      label: 'Compliance',
      icon: <CheckIcon />,
      shortcut: 'G → O',
      action: () => {
        navigate('/compliance')
        setIsOpen(false)
      }
    },
  ], [navigate, setIsOpen])

  // Build items list based on query
  const getItems = useCallback((): PaletteItem[] => {
    const items: PaletteItem[] = []

    if (query.trim().length === 0) {
      // Show quick actions
      items.push(...quickActions.map(action => ({
        id: `action-${action.id}`,
        type: 'action' as const,
        label: action.label,
        icon: action.icon,
        shortcut: action.shortcut,
        onSelect: action.action
      })))

      // Show recent items
      if (recentItems.length > 0) {
        items.push(...recentItems.map(item => ({
          id: `recent-${item.type}-${item.id}`,
          type: 'recent' as const,
          label: item.name,
          sublabel: capitalize(item.type),
          icon: getItemIcon(item.type),
          onSelect: () => {
            navigate(getRecentItemRoute(item))
            setIsOpen(false)
          }
        })))
      }
    } else {
      // Show search results
      items.push(...results.map(result => ({
        id: `result-${result.type}-${result.id}`,
        type: 'result' as const,
        label: result.title,
        sublabel: result.subtitle,
        icon: getItemIcon(result.type === 'client' ? 'athlete' : result.type),
        onSelect: () => {
          navigate(result.url)
          setIsOpen(false)
        }
      })))
    }

    return items
  }, [query, recentItems, results, navigate, quickActions])

  const items = getItems()

  // Global keyboard listener for Cmd+K and chords
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Cmd/Ctrl + K to open
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsOpen(true)
        return
      }

      // Escape to close
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false)
        return
      }

      // Chord shortcuts (only when palette is closed and not in input)
      if (!isOpen && !['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement).tagName)) {
        if (e.key.toLowerCase() === 'n') {
          setChordKey('n')
          setTimeout(() => setChordKey(null), 500)
          return
        }

        if (chordKey === 'n') {
          e.preventDefault()
          switch (e.key.toLowerCase()) {
            case 'd':
              navigate('/deals/new')
              break
            case 'a':
              navigate('/clients/new')
              break
            case 't':
              navigate('/tasks/new')
              break
            case 'p':
              navigate('/prospects/new')
              break
          }
          setChordKey(null)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, chordKey, navigate])

  // Allow external components (e.g. MobileBottomNav) to open the palette
  useEffect(() => {
    function handleOpen() { setIsOpen(true) }
    window.addEventListener('open-command-palette', handleOpen)
    return () => window.removeEventListener('open-command-palette', handleOpen)
  }, [])

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      requestAnimationFrame(() => {
        inputRef.current?.focus()
      })
      // Load recent items
      setRecentItems(getRecentItems())
    } else {
      setQuery('')
      setResults([])
      setSelectedIndex(0)
    }
  }, [isOpen])

  // Search when query changes (with debounce)
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([])
      return
    }

    let cancelled = false
    setLoading(true)

    // Debounce search to avoid API calls on every keystroke
    const debounceTimer = setTimeout(async () => {
      try {
        const results = await Promise.allSettled([
          listClients(),
          listProspects(),
          listDeals()
        ])
        const loadedData = processLoadResults(
          results,
          ['Clients', 'Prospects', 'Deals'],
          false // Don't show toast for command palette
        )
        const clients = loadedData[0] as Client[]
        const prospects = loadedData[1] as Prospect[]
        const deals = loadedData[2] as Deal[]

        if (cancelled) return

        const q = query.toLowerCase()
        const allResults: SearchResult[] = []

        // Search clients
        clients
          .filter(c =>
            c.full_name.toLowerCase().includes(q) ||
            c.team_name?.toLowerCase().includes(q) ||
            c.email?.toLowerCase().includes(q)
          )
          .slice(0, 5)
          .forEach(c => {
            allResults.push({
              type: 'client',
              id: c.id,
              title: c.full_name,
              subtitle: `${c.team_name || 'No team'} · ${c.sport || 'No sport'}`,
              url: `/clients/${c.id}`
            })
          })

        // Search prospects
        prospects
          .filter(p =>
            p.full_name.toLowerCase().includes(q) ||
            p.school?.toLowerCase().includes(q) ||
            p.email?.toLowerCase().includes(q)
          )
          .slice(0, 5)
          .forEach(p => {
            allResults.push({
              type: 'prospect',
              id: p.id,
              title: p.full_name,
              subtitle: `${p.school || 'No school'} · ${p.sport || 'No sport'}`,
              url: `/prospects/${p.id}`
            })
          })

        // Search deals
        deals
          .filter(d =>
            d.title.toLowerCase().includes(q) ||
            d.notes?.toLowerCase().includes(q)
          )
          .slice(0, 5)
          .forEach(d => {
            allResults.push({
              type: 'deal',
              id: d.id,
              title: d.title,
              subtitle: `Deal · ${d.stage}`,
              url: `/deals/${d.id}`
            })
          })

        setResults(allResults.slice(0, 10))
      } catch {
        // Search error - silently fail
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 300) // 300ms debounce delay

    return () => {
      cancelled = true
      clearTimeout(debounceTimer)
    }
  }, [query])

  // Reset selection when items change
  useEffect(() => {
    setSelectedIndex(0)
  }, [items.length])

  // Keyboard navigation within palette
  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(i => Math.min(i + 1, items.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (items[selectedIndex]) {
        items[selectedIndex].onSelect()
      }
    }
  }

  // Closed, this renders nothing at all.
  //
  // It used to return a visible "Search... ⌘K" button. The palette is mounted
  // inside the app layout's flex row, so that button became a layout column:
  // 152px wide and the full height of the page, pinned to the right edge of
  // every screen, with the ⌘K glyph floating in the vertical middle of empty
  // space. It also took 152px away from the content.
  //
  // The entry point it provided is already in the sidebar, which dispatches a
  // synthetic Cmd+K keydown that the listener below picks up. One affordance,
  // in the place people look for it.
  if (!isOpen) return null

  return (
    <>
      <div className={styles.overlay} onClick={() => setIsOpen(false)} role="presentation" />
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Command palette">
        <div className={styles.header}>
          <SearchIcon />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search anything..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className={styles.input}
            aria-label="Search"
          />
          <kbd className={styles.escHint}>ESC</kbd>
        </div>

        <div className={styles.content}>
          {loading && (
            <div className={styles.loading}>Searching...</div>
          )}

          {!loading && query.length >= 2 && results.length === 0 && (
            <div className={styles.noResults}>
              <div className={styles.noResultsIcon}>
                <SearchIcon />
              </div>
              <p className={styles.noResultsText}>No results found for "{query}"</p>
              <p className={styles.noResultsHint}>Try searching for athletes, deals, or prospects</p>
            </div>
          )}

          {!loading && items.length > 0 && (
            <>
              {query.trim().length === 0 && (
                <>
                  <div className={styles.section}>
                    <div className={styles.sectionTitle}>Quick Actions</div>
                    {items.filter(i => i.type === 'action').map((item, idx) => (
                      <button
                        key={item.id}
                        className={`${styles.item} ${selectedIndex === idx ? styles.selected : ''}`}
                        onClick={item.onSelect}
                        onMouseEnter={() => setSelectedIndex(idx)}
                      >
                        <span className={styles.itemIcon}>{item.icon}</span>
                        <span className={styles.itemLabel}>{item.label}</span>
                        {item.shortcut && (
                          <span className={styles.itemShortcut}>{item.shortcut}</span>
                        )}
                      </button>
                    ))}
                  </div>

                  {recentItems.length > 0 && (
                    <div className={styles.section}>
                      <div className={styles.sectionTitle}>Recent</div>
                      {items.filter(i => i.type === 'recent').map((item, idx) => {
                        const actualIdx = quickActions.length + idx
                        return (
                          <button
                            key={item.id}
                            className={`${styles.item} ${selectedIndex === actualIdx ? styles.selected : ''}`}
                            onClick={item.onSelect}
                            onMouseEnter={() => setSelectedIndex(actualIdx)}
                          >
                            <span className={styles.itemIcon}>{item.icon}</span>
                            <div className={styles.itemContent}>
                              <span className={styles.itemLabel}>{item.label}</span>
                              {item.sublabel && (
                                <span className={styles.itemSublabel}>{item.sublabel}</span>
                              )}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </>
              )}

              {query.trim().length >= 2 && results.length > 0 && (
                <div className={styles.section}>
                  <div className={styles.sectionTitle}>Results</div>
                  {items.map((item, idx) => (
                    <button
                      key={item.id}
                      className={`${styles.item} ${selectedIndex === idx ? styles.selected : ''}`}
                      onClick={item.onSelect}
                      onMouseEnter={() => setSelectedIndex(idx)}
                    >
                      <span className={styles.itemIcon}>{item.icon}</span>
                      <div className={styles.itemContent}>
                        <span className={styles.itemLabel}>{item.label}</span>
                        {item.sublabel && (
                          <span className={styles.itemSublabel}>{item.sublabel}</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className={styles.footer}>
          <span className={styles.footerHint}>
            <kbd>↑↓</kbd> Navigate
          </span>
          <span className={styles.footerHint}>
            <kbd>↵</kbd> Select
          </span>
          <span className={styles.footerHint}>
            <kbd>esc</kbd> Close
          </span>
        </div>
      </div>
    </>
  )
}

// Icon components
function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  )
}

function FileIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <polyline points="14,2 14,8 20,8" />
    </svg>
  )
}

function UserIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )
}

function UserPlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="19" y1="8" x2="19" y2="14" />
      <line x1="22" y1="11" x2="16" y2="11" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  )
}

function UsersIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  )
}

function MessageIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  )
}

function CalendarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  )
}

function getItemIcon(type: string): React.ReactNode {
  switch (type) {
    case 'athlete':
    case 'client':
      return <UserIcon />
    case 'prospect':
      return <UserPlusIcon />
    case 'deal':
      return <FileIcon />
    case 'contact':
      return <UsersIcon />
    case 'task':
      return <CheckIcon />
    default:
      return <FileIcon />
  }
}
